/* Login.tsx — the wall, from the browser's side.
 *
 * Everything about it is the same instrument as the rest of the app: hairlines, one accent, no card.
 * The one thing it does NOT do is help an attacker: the server returns a single message for a wrong
 * password, an unknown address and a disabled account, and this screen prints exactly what it was
 * given rather than trying to be more helpful about which one it was.
 */
import { useState } from 'react';
import { api, isErr } from '../lib/api.ts';
import { Btn, Hint, HAIR, selStyle } from '../components/ui.tsx';

export function Login({ onIn }: { onIn: () => void }) {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const r = await api<{ user: unknown }>('auth/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password: pw }),
    });
    setBusy(false);
    if (isErr(r)) { setErr(r.error); return; }
    /* Clear it from memory the moment it is no longer needed. Marginal against a real attacker, but
       it also means a React devtools inspection of this component after a successful sign-in does
       not show the password. */
    setPw('');
    onIn();
  };

  return (
    <div className="atlas-flat" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <form onSubmit={submit} style={{ width: 340 }}>
        {/* Their lockup here too. The sign-in page is the first thing a GrayMatter rep sees, and a
            login screen carrying only a product name reads like somebody else's tool. */}
        <img src="/brand/GMR_logo_white20text_orange20logo_horizontal.svg" alt="GrayMatter Robotics"
          style={{ height: 28, width: 'auto', display: 'block' }} />
        <div className="t-label" style={{ color: 'var(--or)', letterSpacing: '.16em', marginTop: 14 }}>GTM ATLAS</div>
        <div className="t-disp" style={{ marginTop: 2 }}>Sign in</div>
        <Hint style={{ marginTop: 6, marginBottom: 18 }}>
          This dataset carries named people and their direct contact details. Accounts are issued by an
          administrator; there is no self-service signup.
        </Hint>

        <label className="t-label" htmlFor="email">Email</label>
        <input id="email" type="email" autoComplete="username" required autoFocus
          value={email} onChange={(e) => setEmail(e.target.value)}
          style={{ ...selStyle, marginTop: 4, marginBottom: 12 }} />

        <label className="t-label" htmlFor="pw">Password</label>
        <input id="pw" type="password" autoComplete="current-password" required
          value={pw} onChange={(e) => setPw(e.target.value)}
          style={{ ...selStyle, marginTop: 4, marginBottom: 16 }} />

        {/* A real submit button, so Enter in either field signs in. */}
        <Btn wide type="submit" disabled={busy} style={{ textAlign: 'center' }}>
          {busy ? 'signing in…' : 'Sign in'}
        </Btn>

        {err && (
          <div style={{ marginTop: 14, padding: 10, border: HAIR, borderLeft: '2px solid var(--rd)' }}>
            <div className="t-label" style={{ color: 'var(--rd)' }}>Not signed in</div>
            <div style={{ marginTop: 4, color: 'var(--dim)' }}>{err}</div>
          </div>
        )}
      </form>
    </div>
  );
}
